import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";
import {
  getDeepViewFieldKey,
  getDeepViewLineageEntry,
  getDeepViewValuesEntry,
} from "./deepViewFieldMap";

const NON_CURRENCY_FIELDS = new Set(["clicks", "registrations", "ftd", "qftd"]);

interface Props {
  columnKey?: string;
}

export default function DatacollectionLastRunValueFlow({ columnKey }: Props) {
  const deepViewData = useSelector(
    (state: any) => state?.deepView?.deepViewData,
  );
  const currencyData = useSelector(
    (state: any) => state?.deepView?.currencyData,
  );

  const flowData = [
    ...(getDeepViewValuesEntry(deepViewData, columnKey)?.journey_steps ?? []),
  ].sort((a: any, b: any) => a?.step - b?.step);

  const isNotApplicable = (item: any) =>
    item?.is_na === true || item?.value === null || item?.value === undefined;

  const fieldKey = getDeepViewFieldKey(columnKey);

  const isCurrencyField = !!fieldKey && !NON_CURRENCY_FIELDS.has(fieldKey);

  const dealCurrencyCode = getDeepViewLineageEntry(
    deepViewData,
    columnKey,
  )?.fmt_deal_currency_code;

  const dealCurrencySymbol = (currencyData ?? []).find(
    (currency: any) => currency?.currency_code === dealCurrencyCode,
  )?.currency_symbol;

  const getCurrencySymbol = (index: number) => {
    if (!isCurrencyField) return "";
    return index === 0 ? (dealCurrencySymbol ?? "") : "$";
  };

  const formatNumber = (value: number) =>
    value.toLocaleString(
      "en-US",
      isCurrencyField
        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        : { maximumFractionDigits: 2 },
    );

  const getDisplayValue = (item: any, index: number) => {
    if (isNotApplicable(item)) return "-";
    const numericValue = Number(item.value);
    if (isNaN(numericValue)) return item.value;
    const sign = numericValue < 0 ? "-" : "";
    return `${sign}${getCurrencySymbol(index)}${formatNumber(Math.abs(numericValue))}`;
  };

  const getValueColor = (item: any, isLastStep: boolean) => {
    if (isLastStep) return "#9C27B0";
    if (isNotApplicable(item) || Number(item.value) === 0) return "#00000061";
    const value = Number(item.value);
    if (!isNaN(value) && value < 0) {
      return "#C62828";
    }
    return "#000000DE";
  };

  if (flowData.length === 0) return null;

  return (
    <Box
      sx={{
        border: "1px solid #BDBDBD",
        borderRadius: "5px",
        backgroundColor: "#FAFAFA",
        p: 1,
        overflowX: "auto",
        width: "100%",
        mt: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: "#00000099",
          letterSpacing: "0.46px",
          mb: 0.5,
        }}
      >
        VALUE FLOW - HOW WE GOT THIS NUMBER
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="flex-start">
        {flowData.map((item: any, index: number) => (
          <React.Fragment key={item.step ?? index}>
            <Box
              sx={{
                textAlign: "center",
                pr: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: "#00000099",
                  mb: 0.5,
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: getValueColor(item, index === flowData.length - 1),
                }}
              >
                {getDisplayValue(item, index)}
              </Typography>

              <Typography
                sx={{
                  fontSize: 8,
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: "#00000099",
                  mt: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                {item.sub}
              </Typography>

              {/* Fixed height so stages without a dot stay vertically aligned */}
            </Box>

            {index < flowData.length - 1 && (
              <ArrowForwardIcon
                sx={{
                  color: "#00000061",
                  fontSize: 12,
                  mx: 1,
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
}
